/**
 * The identity photograph — design/09 §3.
 *
 * §1 established Bata's brand language from the production CSS, and §3 names what it is:
 * "retail-editorial — 4rem uppercase condensed headlines, FULL-BLEED PRODUCT
 * PHOTOGRAPHY, red as an attention device, generous whitespace". §3 then draws the line
 * this repository is built around: the brand lives in "logo lockup, header, navigation,
 * section headers, EMPTY STATES, buttons, the warm neutral ramp" and is "DELIBERATELY
 * ABSENT FROM THE DATA SURFACE, where colour is reserved for semantics".
 *
 * Photography is therefore admissible here and nowhere near a grid. This component is
 * used by the two screens that touch no data region at all — the sign-in gate and the
 * Keycloak callback — and by the Cockpit's opening band under ADR 0086's amendment. See ADR 0086 for why the 404 and the
 * no-permission surfaces were left alone even though §3's sentence names empty states.
 *
 * THE POOL is `hero-photographs.json`: twenty photographs across men's, women's and
 * kids', and each visit picks one at random (ADR 9017). THE RENDITIONS are built from it
 * by `node tools/build-brand-imagery.mjs`, which verifies each source's sha256. They are not the estate's article renditions
 * (96/240/480/960 square): a hero is sized by the viewport, so the ladder is design/09
 * §2.2's breakpoints — 375 / 768 / 1024 / 1200 / 1440.
 *
 * `sizes` is not decoration. The photograph is the full viewport width below `md` and
 * exactly half of it above, which is what `SignInSurface` lays out; stating that here is
 * what lets a 375px phone fetch 6 kB instead of 59 kB.
 */

import photographs from './hero-photographs.json';

/** Kept in step with `WIDTHS` in tools/build-brand-imagery.mjs. */
export const HERO_WIDTHS = [375, 768, 1024, 1200, 1440] as const;

/**
 * The byte budget, stated once, here, beside the thing it constrains. Every limit but
 * `total` is per photograph, and a visit fetches ONE photograph — so the pool's size is
 * repository weight, never page weight.
 *
 * G2 §2.1 budgets `screen.interactive.cold` at 3000 ms p95. The hero does not block
 * first paint and does not block the sign-in button; it competes for bandwidth only. At
 * 80 kB on a 5 Mbit/s cold link that is about 130 ms — a twenty-third of the budget — and
 * the ladder means a 375 px phone fetches 6 kB rather than 59 kB.
 *
 * `perCommonViewport` is the one worth reading twice. Because `SignInSurface` gives the
 * photograph 50vw from `md` up, a 1× viewport never asks for more than 768 CSS px: a
 * 1440 px desktop at 1× renders it at 720 and is served the 768 tier. The larger tiers
 * exist for DPR 2, where the device is by definition not the constrained one.
 *
 * This is deliberately NOT a row in `src/test/performance-budgets.ts`. That file is §2.1
 * verbatim — eight rows, each a p95 DURATION, with a test asserting none has been
 * dropped or added. A byte ceiling on one decorative asset is not a §2.1 row and adding
 * it would make that file's completeness assertion a lie.
 *
 * The gate is `BrandHero.test.tsx`, which runs in CI. `tools/build-brand-imagery.mjs`
 * reports against these same numbers by parsing this declaration, because CI has no copy
 * of the source photographs and cannot run the builder at all.
 */
export const HERO_BYTE_BUDGET = {
  /** Any single rendition. Only the 1440 tier comes near it. */
  perRendition: 80 * 1024,
  /** Every tier a 1× viewport can ask for — that is, everything up to 768 px wide. */
  perCommonViewport: 30 * 1024,
  /** One photograph's whole ladder. */
  perPhotograph: 200 * 1024,
  /** Everything committed under public/brand/hero/ — twenty ladders. */
  total: 4000 * 1024,
} as const;

const HERO_BASE = '/brand/hero';

/**
 * A photograph the hero can show. `productName` is the storefront's own name for the
 * article, copied from the capture's manifest row — Spanish, because the storefront is
 * Bata Colombia's, and not a catalogue key, because it is data and not copy.
 */
export interface HeroPhotograph {
  readonly id: string;
  readonly article: string;
  readonly productName: string;
}

/**
 * The manifest rows carry the storefront's spacing verbatim, doubled and trailing spaces
 * included ("Red Label  Ibsen"). The caption shows the same name with its whitespace
 * collapsed; the JSON keeps the row as captured, because it is the provenance.
 */
export const HERO_PHOTOGRAPHS: readonly HeroPhotograph[] = photographs.map(
  ({ id, article, productName }) => ({ id, article, productName: productName.replace(/\s+/g, ' ').trim() }),
);

/**
 * One photograph, uniformly at random. `random` is injectable so the choice is tested
 * rather than eyed; the caller holds the result in state so it does not change mid-visit.
 */
export function pickHeroPhotograph(random: () => number = Math.random): HeroPhotograph {
  const index = Math.min(HERO_PHOTOGRAPHS.length - 1, Math.floor(random() * HERO_PHOTOGRAPHS.length));
  const photograph = HERO_PHOTOGRAPHS[index];
  if (photograph === undefined) throw new Error('hero-photographs.json is empty');
  return photograph;
}

export const heroSource = (id: string, width: number): string => `${HERO_BASE}/${id}-${width}.webp`;

export interface BrandHeroProps {
  readonly className?: string;
  /**
   * `split` — half the sign-in screen from `md` up (the original). `band` — the Cockpit's
   * opening band, the width of the content column, under an ink veil. Both are black and
   * white (designer audit 8.2 for the sign-in; ADR 0086 as amended for the band): a CSS
   * filter over the photograph, not a colour.
   * The variant sets `sizes`, which is what decides the rendition a device fetches.
   */
  readonly variant?: 'split' | 'band';
  /** Which photograph. The caller chooses — usually with `pickHeroPhotograph` — so a caption can name it. */
  readonly photograph: HeroPhotograph;
}

export function BrandHero({ className, variant = 'split', photograph }: BrandHeroProps): React.JSX.Element {
  return (
    <img
      /**
       * DECORATIVE, and deliberately so. WCAG 1.1.1 asks for a text alternative that
       * serves the same purpose as the image; this photograph's purpose is atmosphere.
       * The identity it carries is already announced by `BataWordmark`, whose
       * `aria-label` is "Bata", and nothing in the picture is needed to sign in.
       * Describing a cognac leather boot to somebody who has come here to type a
       * password is noise placed between them and the button — so `alt=""` takes it out
       * of the accessibility tree entirely, which is the documented meaning of an empty
       * alt and not an omission. The product's NAME is on the sign-in screen as visible
       * text (ADR 9017), in the caption beside the photograph, and so is read in the
       * page's flow; repeating it as alt would announce it twice.
       */
      alt=""
      src={heroSource(photograph.id, 1024)}
      srcSet={HERO_WIDTHS.map((width) => `${heroSource(photograph.id, width)} ${width}w`).join(', ')}
      sizes={variant === 'band'
        // The content column: the viewport less the rail, wide or collapsed.
        ? '(min-width: 1200px) calc(100vw - 240px), calc(100vw - 56px)'
        : '(min-width: 1024px) 50vw, 100vw'}
      /**
       * Off the critical path. G2 §2.1 budgets `screen.interactive.cold` at 3000 ms and
       * the thing that must arrive inside it is the sign-in button, not the leather.
       */
      decoding="async"
      className={['size-full object-cover grayscale', className ?? ''].join(' ')}
    />
  );
}
